// Attachment preview beyond a generic type icon, read from the local `File`
// object -- the composer's pre-send tiles, and a sent image until a reload
// drops the `File` (ChatMessage.tsx then fetches the uploaded artifact).
//
// office documents (.docx/.pptx) have no native browser renderer and no
// installed parsing library -- they stay on the generic icon. Adding one
// (e.g. mammoth.js for docx; nothing lightweight exists for pptx slide
// thumbnails) is a real dependency decision, not something to reach for
// here.

import { useEffect, useState } from 'react';
import { extensionOf } from './uploadConstraints';
import { isImageFile } from './fileTypeIcon';

export type FilePreview =
  | { kind: 'image'; src: string }
  | { kind: 'pdf'; src: string }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'text'; snippet: string }
  | { kind: 'none' };

const TABLE_PREVIEW_MAX_ROWS = 6;
const TABLE_PREVIEW_MAX_COLS = 4;
const TABULAR_EXTENSIONS = new Set(['.csv', '.tsv', '.xlsx', '.xls']);
const TEXT_PREVIEW_MAX_CHARS = 400;
const TEXT_EXTENSIONS = new Set(['.txt', '.md', '.json', '.yaml', '.yml', '.xml', '.toml', '.html', '.css']);

function isPdfFile(contentType: string | undefined, ext: string): boolean {
  return contentType === 'application/pdf' || ext === '.pdf';
}

// Reuses the xlsx (SheetJS) package already installed for markdown export
// (entities/message/model/markdownExport.ts) instead of hand-rolling CSV
// quote/escape parsing -- it reads both delimited text and real workbooks
// through the same API.
async function readTablePreview(file: File, ext: string): Promise<string[][] | null> {
  const XLSX = await import('xlsx');
  const workbook =
    ext === '.csv' || ext === '.tsv'
      ? XLSX.read(await file.text(), { type: 'string' })
      : XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0] ?? ''];
  if (!sheet) return null;
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });
  if (rows.length === 0) return null;
  return rows
    .slice(0, TABLE_PREVIEW_MAX_ROWS)
    .map((row) => row.slice(0, TABLE_PREVIEW_MAX_COLS).map((cell) => (cell == null ? '' : String(cell))));
}

// JSON gets pretty-printed for readability; every other text extension
// (md/yaml/xml/toml/html/css/txt) is already human-readable as-is.
async function readTextPreview(file: File, ext: string): Promise<string> {
  const raw = await file.text();
  if (ext !== '.json') return raw.slice(0, TEXT_PREVIEW_MAX_CHARS);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2).slice(0, TEXT_PREVIEW_MAX_CHARS);
  } catch {
    return raw.slice(0, TEXT_PREVIEW_MAX_CHARS);
  }
}

export function useFilePreview(file: File | undefined, contentType: string | undefined): FilePreview {
  const [preview, setPreview] = useState<FilePreview>({ kind: 'none' });

  useEffect(() => {
    if (!file) {
      setPreview({ kind: 'none' });
      return;
    }

    const ext = extensionOf(file.name);

    const image = isImageFile(contentType, file.name);
    if (image || isPdfFile(contentType, ext)) {
      const url = URL.createObjectURL(file);
      setPreview(image ? { kind: 'image', src: url } : { kind: 'pdf', src: url });
      return () => URL.revokeObjectURL(url);
    }

    if (TABULAR_EXTENSIONS.has(ext)) {
      let cancelled = false;
      setPreview({ kind: 'none' });
      readTablePreview(file, ext)
        .then((rows) => {
          if (!cancelled && rows) setPreview({ kind: 'table', rows });
        })
        .catch(() => {
          // Malformed/unreadable workbook -- keep the generic icon.
        });
      return () => {
        cancelled = true;
      };
    }

    if (TEXT_EXTENSIONS.has(ext)) {
      let cancelled = false;
      setPreview({ kind: 'none' });
      readTextPreview(file, ext)
        .then((snippet) => {
          if (!cancelled) setPreview({ kind: 'text', snippet });
        })
        .catch(() => {
          // Unreadable file -- keep the generic icon.
        });
      return () => {
        cancelled = true;
      };
    }

    setPreview({ kind: 'none' });
    return undefined;
  }, [file, contentType]);

  return preview;
}
